// ✅ VehiculesPage.js
import React, { useEffect, useState } from "react";
import "../styles/Vehicules.css";
import "../styles/responsive.css";
import { FaSearch, FaPlus, FaPen, FaTrash, FaCar, FaWeight, FaArrowsAlt, FaGasPump, FaCogs, FaUserFriends } from "react-icons/fa";
import { useVehicules, useVehiculeActions, useVehicleTypes } from "../hooks/useVehicules";
import { useSearch } from '../contexts/SearchContext';
import useCurrentPrestataire from "../hooks/useCurrentPrestataire";

export default function VehiculesPage() {
  const { vehicules, loading, error, fetchVehicules } = useVehicules();
  const { getVehicule, createVehicule, updateVehicule, deleteVehicule } = useVehiculeActions();
  const { categories, categoryMapping, vehicleTypes, loading: typesLoading } = useVehicleTypes();
  const { globalSearch } = useSearch();
  const { prestataire_id, loading: prestataireLoading, error: prestataireError } = useCurrentPrestataire();
  
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [showModal, setShowModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showDeleteId, setShowDeleteId] = useState(null);
  const [currentVehicleData, setCurrentVehicleData] = useState(null);

  const [vehiculeName, setVehiculeName] = useState("");
  const [vehiculeSlug, setVehiculeSlug] = useState("");
  const [vehiculeCategory, setVehiculeCategory] = useState("");
  const [vehiculeWeight, setVehiculeWeight] = useState("");
  const [vehiculeLength, setVehiculeLength] = useState("");
  const [vehiculeWidth, setVehiculeWidth] = useState("");
  const [vehiculeHeight, setVehiculeHeight] = useState("");
  const [vehiculeFuel, setVehiculeFuel] = useState("");
  const [vehiculeTransmission, setVehiculeTransmission] = useState("");
  const [vehiculeCapacity, setVehiculeCapacity] = useState("");

  // Utiliser la configuration centralisée de l'API
  const { API_BASE_URL: apiBaseUrl, API_SERVER_URL: SERVER_BASE_URL } = require('../config/api');

  useEffect(() => {
    fetchVehicules({ search, category: activeCategory });
  }, [search, activeCategory, fetchVehicules]);

  const getVehicleTypeId = (category) => {
    // Utiliser le mapping dynamique récupéré de l'API
    if (categoryMapping && categoryMapping[category]) {
      return categoryMapping[category];
    }
    // Si pas de mapping disponible, retourner le premier type disponible ou null
    if (vehicleTypes && vehicleTypes.length > 0) {
      // Chercher un type correspondant à la catégorie
      const matchingType = vehicleTypes.find(t =>
        t.name?.toLowerCase().includes(category.toLowerCase()) ||
        t.category?.toLowerCase() === category.toLowerCase()
      );
      return matchingType?.id || vehicleTypes[0].id;
    }
    console.warn('⚠️ Aucun type de véhicule disponible depuis l\'API');
    return null;
  };

  const getVehicleCategory = (vehicleTypeName) => {
    if (!vehicleTypeName) return 'Non catégorisé';
    const lowerName = vehicleTypeName.toLowerCase();
    if (lowerName.includes('pelleteuse') || lowerName.includes('excavatrice')) return 'Engin';
    if (lowerName.includes('camion-benne')) return 'Camion';
    if (lowerName.includes('fourgon')) return 'Camionnette';
    if (lowerName.includes('4x4')) return 'Voiture';
    if (lowerName.includes('moto')) return 'Moto';
    return 'Autre';
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      console.log("Image sélectionnée:", files[0].name);
      setSelectedFiles(files);
      const reader = new FileReader();
      reader.onload = (e) => setUploadedImage(e.target.result);
      reader.readAsDataURL(files[0]);
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    
    try {
      // Extraire marque et modèle du nom
      const [brand, ...modelParts] = vehiculeName.split(' ');
      const model = modelParts.join(' ') || brand;
      
      // Trouver l'ID du type de véhicule basé sur la catégorie
      const vehicleTypeId = await getVehicleTypeId(vehiculeCategory);
      
      if (editId && currentVehicleData) {
        // Utiliser les données existantes pour la modification
        formData.append("prestataire_id", currentVehicleData.prestataire_id);
        formData.append("vehicle_type_id", vehicleTypeId);
        formData.append("brand", brand);
        formData.append("model", model);
        formData.append("year", currentVehicleData.year);
        formData.append("license_plate", currentVehicleData.license_plate);
        formData.append("color", currentVehicleData.color);
        formData.append("insurance_number", currentVehicleData.insurance_number);
        formData.append("insurance_expiry", currentVehicleData.insurance_expiry.split('T')[0]);
        formData.append("technical_control_expiry", currentVehicleData.technical_control_expiry.split('T')[0]);
        
        // Ajouter les dimensions (toujours envoyer, même si vides - le backend gère les chaînes vides)
        formData.append("length_m", vehiculeLength || "");
        formData.append("width_m", vehiculeWidth || "");
        formData.append("height_m", vehiculeHeight || "");
        formData.append("capacity_weight_kg", vehiculeWeight || "");
        
        // Ajouter les photos seulement si de nouvelles photos sont sélectionnées
        selectedFiles.forEach((file) => formData.append("photos", file));
        
        console.log("Envoi de la mise à jour du véhicule:", editId);
        const result = await updateVehicule(editId, formData);
        console.log("Résultat de la mise à jour:", result);
      } else {
        // Nouveau véhicule
        if (!prestataire_id) {
          alert('Impossible de créer le véhicule: profil prestataire non trouvé');
          return;
        }
        formData.append("prestataire_id", prestataire_id);
        formData.append("vehicle_type_id", vehicleTypeId);
        formData.append("brand", brand);
        formData.append("model", model);
        formData.append("year", new Date().getFullYear());
        formData.append("license_plate", `XX-${Math.floor(Math.random() * 900) + 100}-YY`);
        formData.append("color", "Standard");
        formData.append("insurance_number", `INS-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000)}`);
        formData.append("insurance_expiry", new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]);
        formData.append("technical_control_expiry", new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]);
        
        // Ajouter les dimensions
        formData.append("length_m", vehiculeLength || "");
        formData.append("width_m", vehiculeWidth || "");
        formData.append("height_m", vehiculeHeight || "");
        formData.append("capacity_weight_kg", vehiculeWeight || "");
        
        // Ajouter les photos pour les nouveaux véhicules
        selectedFiles.forEach((file) => formData.append("photos", file));
        
        console.log("Création d'un nouveau véhicule");
        const result = await createVehicule(formData);
        console.log("Résultat de la création:", result);
      }
      
      // Fermer le modal et réinitialiser le formulaire
      setShowModal(false);
      resetForm();
      
      // Rafraîchir la liste des véhicules
      console.log("Rafraîchissement de la liste des véhicules...");
      await fetchVehicules({ search, category: activeCategory });
    } catch (err) {
      console.error("Erreur enregistrement:", err);
    }
  };

  const handleEdit = async (id) => {
    try {
      const data = await getVehicule(id);
      setEditId(id);
      setCurrentVehicleData(data);
      setVehiculeName(`${data.brand} ${data.model}`);
      setVehiculeSlug(data.vehicle_type_name);
      setVehiculeCategory(getVehicleCategory(data.vehicle_type_name));
      setVehiculeCapacity(data.capacity_passengers || "");
      setVehiculeWeight(data.capacity_weight_kg || "");
      setVehiculeLength(data.length_m || "");
      setVehiculeWidth(data.width_m || "");
      setVehiculeHeight(data.height_m || "");
      setVehiculeFuel(data.fuel_type || "");
      setVehiculeTransmission(data.transmission || "");
      setUploadedImage(data.photos?.[0] ? data.photos[0].startsWith('http') ? data.photos[0] : `${SERVER_BASE_URL}${data.photos[0]}` : null);
      setShowModal(true);
    } catch (err) {
      console.error("Erreur get by id:", err);
    }
  };

  const handleDelete = async () => {
    try {
      console.log("Suppression du véhicule:", showDeleteId);
      await deleteVehicule(showDeleteId);
      console.log("Véhicule supprimé avec succès");
      setShowDeleteId(null);
      fetchVehicules({ search, category: activeCategory });
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert("Erreur lors de la suppression: " + err.message);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setVehiculeName("");
    setVehiculeSlug("");
    setVehiculeCategory("");
    setVehiculeWeight("");
    setVehiculeLength("");
    setVehiculeWidth("");
    setVehiculeHeight("");
    setVehiculeFuel("");
    setVehiculeTransmission("");
    setVehiculeCapacity("");
    setUploadedImage(null);
    setSelectedFiles([]);
    setCurrentVehicleData(null);
    // Réinitialiser l'input file
    const fileInput = document.getElementById('imageUpload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div className="vehicules-container">
      <h2 className="vehicules-title">Véhicules</h2>
      <div className="vehicules-toolbar">
        <div className="vehicules-search">
          <FaSearch className="search-icon" />
          <input type="text" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="vehicules-categories-inline">
          {categories.map((cat) => (
            <button key={cat} className={cat === activeCategory ? "active" : ""} onClick={() => setActiveCategory(cat)}>
              {cat}
            </button>
          ))}
        </div>

        <button className="vehicules-ajouter" onClick={() => { resetForm(); setShowModal(true); }}>
          <FaPlus /> Ajouter un véhicule
        </button>
      </div>

      <div className="vehicules-grid">
        {vehicules.filter(v => {
          // Filtrage par recherche globale (du header)
          const matchesGlobalSearch = !globalSearch || 
            v.brand?.toLowerCase().includes(globalSearch.toLowerCase()) ||
            v.model?.toLowerCase().includes(globalSearch.toLowerCase()) ||
            v.vehicle_type_name?.toLowerCase().includes(globalSearch.toLowerCase()) ||
            v.license_plate?.toLowerCase().includes(globalSearch.toLowerCase());
          
          // Filtrage par recherche locale (de la page)
          const matchesLocalSearch = !search ||
            v.brand?.toLowerCase().includes(search.toLowerCase()) ||
            v.model?.toLowerCase().includes(search.toLowerCase()) ||
            v.vehicle_type_name?.toLowerCase().includes(search.toLowerCase()) ||
            v.license_plate?.toLowerCase().includes(search.toLowerCase());

          // Filtrage par catégorie
          const matchesCategory = activeCategory === "Tous" || 
            v.vehicle_type_name?.toLowerCase().includes(activeCategory.toLowerCase());

          return matchesGlobalSearch && matchesLocalSearch && matchesCategory;
        }).map((v) => {
          const getCardClass = (vehicleTypeName) => {
            if (!vehicleTypeName) return 'bulldozer'; // default si pas de nom
            const lowerName = vehicleTypeName.toLowerCase();
            if (lowerName.includes('bulldozer')) return 'bulldozer';
            if (lowerName.includes('excavatrice') || lowerName.includes('pelleteuse')) return 'excavatrice';
            if (lowerName.includes('grue')) return 'grue';
            if (lowerName.includes('camion') && lowerName.includes('benne')) return 'camion-benne';
            if (lowerName.includes('camion')) return 'camion';
            if (lowerName.includes('camionnette')) return 'camionnette';
            if (lowerName.includes('berline')) return 'berline';
            if (lowerName.includes('suv') || lowerName.includes('4x4')) return 'suv';
            if (lowerName.includes('minibus') || lowerName.includes('bus')) return 'minibus';
            if (lowerName.includes('fourgon')) return 'camionnette';
            if (lowerName.includes('moto')) return 'minibus'; // Utiliser la couleur cyan pour les motos
            return 'bulldozer'; // default
          };

          return (
            <div key={v.id} className="vehicule-card">
              <div className={`vehicule-card-header ${getCardClass(v.vehicle_type_name || v.brand)}`}>
                {v.icon_url && (
                  <div className="vehicule-icon">
                    <img 
                      src={`${SERVER_BASE_URL}${v.icon_url}`} 
                      alt={`${v.name} icon`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                
                <img 
                  className="vehicule-card-image"
                  src={v.photos?.[0] ? `${SERVER_BASE_URL}${v.photos[0]}` : "/assets/default.png"} 
                  alt={v.name} 
                  onError={(e) => { e.target.src = "/assets/default.png"; }}
                />
                
                <div className="vehicule-actions">
                  <button className="action-btn" onClick={() => handleEdit(v.id)}>
                    <img src="/assets/icons/pencil.png" alt="Modifier" className="action-icon" />
                  </button>
                  <button className="action-btn" onClick={() => setShowDeleteId(v.id)}>
                    <img src="/assets/icons/trash-2.png" alt="Supprimer" className="action-icon" />
                  </button>
                </div>
              </div>
              
              <div className="vehicule-content">
                <h4>{(v.brand && v.model) ? `${v.brand} ${v.model}` : v.vehicle_type_name}</h4>
                <span className="categorie">Catégorie : {getVehicleCategory(v.vehicle_type_name)}</span>
                <div className="details">
                  <div className="detail-item">
                    <FaGasPump className="icon" />
                    <span>{v.fuel_type === 'diesel' ? 'Diesel' : v.fuel_type === 'essence' ? 'Essence' : 'Non spécifié'}</span>
                  </div>
                  <div className="detail-item">
                    <FaCogs className="icon" />
                    <span>{v.transmission === 'manuel' ? 'Manuelle' : v.transmission === 'automatique' ? 'Automatique' : 'Non spécifié'}</span>
                  </div>
                  {/* Tonnage et volume pour camions */}
                  {(v.vehicle_type_name?.toLowerCase().includes('camion') || v.vehicle_type_name?.toLowerCase().includes('fourgon')) && (
                    <div className="detail-item">
                      <FaWeight className="icon" />
                      <span>{v.capacity_weight_kg ? Math.round(v.capacity_weight_kg / 1000) : 'N/A'}T /{v.capacity_volume_m3 || 'N/A'} m³</span>
                    </div>
                  )}
                  {/* Nombre de places pour véhicules de transport */}
                  {(v.vehicle_type_name?.toLowerCase().includes('4x4') || v.vehicle_type_name?.toLowerCase().includes('voiture')) && (
                    <div className="detail-item">
                      <FaUserFriends className="icon" />
                      <span>{v.capacity_passengers || 'N/A'} places</span>
                    </div>
                  )}
                  {/* Dimensions pour engins */}
                  {v.vehicle_type_name?.toLowerCase().includes('pelleteuse') && (
                    <div className="detail-item dimensions">
                      <span className="dimension-label">T : {v.capacity_weight_kg ? Math.round(v.capacity_weight_kg / 1000) : 'N/A'}</span>
                      <span className="dimension-label">L : {v.length_m || 'N/A'}m</span>
                      <span className="dimension-label">l : {v.width_m || 'N/A'}m</span>
                      <span className="dimension-label">h : {v.height_m || 'N/A'}m</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="popup-overlay" onClick={() => setShowModal(false)}>
          <div className="vehicule-popup-figma" onClick={(e) => e.stopPropagation()}>
            <div className="vehicule-popup-header-figma">
              <h3>{editId ? "Modification de véhicule" : "Ajout de véhicule"}</h3>
              <button className="popup-close-figma" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="vehicule-image-section">
              <div className="vehicule-image-preview" onClick={() => document.getElementById('imageUpload').click()}>
                {uploadedImage ? (
                  <img src={uploadedImage} alt="Véhicule" style={{cursor: 'pointer'}} />
                ) : (
                  <div className="image-placeholder" style={{cursor: 'pointer'}}>
                    <img src="/assets/default-vehicle.png" alt="Véhicule" />
                  </div>
                )}
                <div className="image-upload-overlay" onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('imageUpload').click();
                }}>
                  <span>Cliquer pour modifier l'image</span>
                </div>
              </div>
            </div>

            <div className="vehicule-form-figma">
              <div className="form-group-figma">
                <label>Nom du véhicule</label>
                <input 
                  type="text" 
                  value={vehiculeName} 
                  onChange={(e) => setVehiculeName(e.target.value)}
                  placeholder="Pelleteuse / Excavatrice"
                  className="input-compact"
                />
              </div>

              <div className="form-group-figma">
                <label>Type de véhicule</label>
                <select value={vehiculeCategory} onChange={(e) => setVehiculeCategory(e.target.value)} className="select-compact">
                  <option value="Engin">Engin</option>
                  <option value="Camion">Camion</option>
                  <option value="Camionnette">Camionnette</option>
                  <option value="Voiture">Voiture</option>
                  <option value="Moto">Moto</option>
                </select>
              </div>

              <div className="form-group-tonnage-compact">
                <label>
                  <img src="/assets/icons/weight.svg" alt="Poids" className="form-icon-small" />
                  Tonnage
                </label>
                <div className="tonnage-input-compact">
                  <input 
                    type="number" 
                    value={vehiculeWeight}
                    onChange={(e) => setVehiculeWeight(e.target.value)}
                    placeholder="3500,00"
                  />
                  <span className="unit-compact">T</span>
                </div>
              </div>

              <div className="form-group-dimensions-compact">
                <label className="dimensions-main-label">
                  <img src="/assets/icons/ruler.svg" alt="Dimensions" className="form-icon-small" />
                  Dimensions (L x l x h)
                </label>
                <div className="dimensions-row-compact">
                  <div className="dimension-input-compact">
                    <input 
                      type="number" 
                      value={vehiculeLength}
                      onChange={(e) => setVehiculeLength(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="dimension-input-compact">
                    <input 
                      type="number" 
                      value={vehiculeWidth}
                      onChange={(e) => setVehiculeWidth(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="dimension-input-compact">
                    <input 
                      type="number" 
                      value={vehiculeHeight}
                      onChange={(e) => setVehiculeHeight(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <span className="unit-dimensions">m</span>
                </div>
              </div>

              <div className="form-group-figma">
                <label>
                  <img src="/assets/icons/fuel.svg" alt="Carburant" className="form-icon-small" />
                  Carburant
                </label>
                <select value={vehiculeFuel} onChange={(e) => setVehiculeFuel(e.target.value)} className="select-compact">
                  <option value="">Sélectionner</option>
                  <option value="diesel">Diesel</option>
                  <option value="essence">Essence</option>
                </select>
              </div>

              <div className="form-group-figma">
                <label>
                  <img src="/assets/icons/gear.svg" alt="Boîte de vitesse" className="form-icon-small" />
                  Boîte de vitesse
                </label>
                <select value={vehiculeTransmission} onChange={(e) => setVehiculeTransmission(e.target.value)} className="select-compact">
                  <option value="">Sélectionner</option>
                  <option value="manuel">Manuelle</option>
                  <option value="automatique">Automatique</option>
                </select>
              </div>

              <div className="form-group-places-compact">
                <label>
                  <img src="/assets/icons/users.svg" alt="Nombre de places" className="form-icon-small" />
                  Nombre de places
                </label>
                <div className="places-input-compact">
                  <input 
                    type="number" 
                    value={vehiculeCapacity} 
                    onChange={(e) => setVehiculeCapacity(e.target.value)}
                    placeholder="1"
                  />
                  <span className="unit-places">Personne(s)</span>
                </div>
              </div>

              <input type="file" id="imageUpload" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
            </div>

            <div className="vehicule-popup-actions-figma">
              <button className="btn-cancel-figma" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-submit-figma" onClick={handleSubmit}>
                {editId ? "Modifier" : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteId && (
        <div className="popup-overlay" onClick={() => setShowDeleteId(null)}>
          <div className="vehicule-popup" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmer la suppression ?</h3>
            <div className="vehicule-popup-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteId(null)}>Annuler</button>
              <button className="btn-submit" onClick={handleDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
